import React, { useEffect, useState, useRef } from "react";
import ConfirmModal from "../components/ConfirmModal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import API_BASE_URL from "../api";
import { authFetch } from "../authFetch";
import AddStreamModal from "../components/AddStreamModal";
import AddObserverModal from "../components/AddObserverModal";
import Toast from "../components/Toast";
import { useAuth } from "../authContext";
import { useTranslation } from "react-i18next";
import { useLocale, fmtDateTime } from "../i18n/format";

// ===== Константы API =====
const API = (API_BASE_URL || "").replace(/\/+$/, "");

export default function SilenceSettingsPage({ setCartItems, setIsCartOpen }) {
  // ----------------- СТЕЙТИ -----------------
  const toastTimer = useRef(null);
  const { isAuth, user } = useAuth();
  const isDemo = String(user?.email || "").toLowerCase() === "demo@radiomonitor.net";
  const { t } = useTranslation("common");
  const locale = useLocale();
  const [toast, setToast] = useState({ open: false, text: "", type: "info" });
  const showToast = (text, type = "info") => {
    setToast({ open: true, text, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast({ open: false, text: "", type }), 3200);
  };

  // ЧАСТЬ 1: URL + ПОТОКИ
  const [url, setUrl] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [streamName, setStreamName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [streams, setStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [cartAddingId, setCartAddingId] = useState(null);
  const streamsLoadingRef = useRef(false);

  // ── підтвердження додавання платного потоку ──
  const [confirmAddOpen, setConfirmAddOpen] = useState(false);
  const [pendingStream, setPendingStream] = useState({ url: "", name: "" });

  // ЧАСТЬ 2: СПОСТЕРІГАЧІ
  const [list, setList] = useState([]); // [{email, active}]
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteEmail, setPendingDeleteEmail] = useState(null);

  // Общие
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const ensureAuthorized = () => {
    if (!isAuth) {
      setShowLoginAlert(true);
      return false;
    }
    return true;
  };

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  // ----------------- API: Streams -----------------
  const fetchStreams = async (silent = false) => {
    if (streamsLoadingRef.current) return;
    streamsLoadingRef.current = true;
    if (!silent) setLoadingStreams(true);
    try {
      if (!isAuth) {
        setStreams([]);
        return;
      }
      const res = await authFetch(`${API}/streams`);
      if (res.ok) {
        const data = await res.json();
        setStreams(Array.isArray(data) ? data : []);
      } else {
        setStreams([]);
      }
    } catch {
      setStreams([]);
    } finally {
      if (!silent) setLoadingStreams(false);
      if (!isAuth) setLoadingStreams(false);
      streamsLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (isAuth) {
      fetchStreams();
    } else {
      setStreams([]);
      setLoadingStreams(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // Поллинг статусів потоків (20с), пауза у фоні, миттєвий рефреш при фокусі
  useEffect(() => {
    if (!isAuth) return;
    const tick = () => {
      if (document.hidden || !isAuth) return;
      fetchStreams(true);
    };
    const id = setInterval(tick, 20000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  function checkUrlValid(str) {
    return /^https?:\/\/[^ ]+\.[^ ]+/.test(str.trim());
  }

  async function handleUrlChange(e) {
    const value = e.target.value;
    setUrl(value);
    setIsValid(false);
    setCheckError("");
    if (checkUrlValid(value)) {
      setChecking(true);
      try {
        const res = await fetch(`${API}/streams/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ stream_url: value.trim() }),
        });
        const data = await res.json();
        setIsValid(Boolean(data.ok));
        setCheckError(data.ok ? "" : data.reason || "Недійсний потік");
      } catch {
        setIsValid(false);
        setCheckError("Помилка перевірки потоку");
      }
      setChecking(false);
    }
  }

  async function handleAddStream() {
    if (!ensureAuthorized()) return;
    try {
      const bodyData = {
        stream_url: url.trim(),
        name: streamName.trim(),
      };
      const res = await authFetch(`${API}/streams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data?.detail;
        const msg = typeof detail === "string" ? detail : "";

        // ⛔ DEMO: сервер вернул блокировку — глобальный перехватчик уже поднимет модалку
        if (
          res.status === 403 &&
          detail &&
          (detail.code === "DEMO_ADD_BLOCKED" || detail?.error === "DEMO_ADD_BLOCKED")
        ) {
          return;
        }

        // тест уже використано — показуємо модалку підтвердження
        if (msg.includes("Тестовий період") && msg.includes("подальшою оплатою")) {
          setPendingStream({ url: url.trim(), name: streamName.trim() });
          setConfirmAddOpen(true);
          return;
        }

        showToast(msg || t("silence.toast.add_stream_error"), "error");
        return;
      }

      // успех как было
      setUrl("");
      setStreamName("");
      setShowAddModal(false);
      await fetchStreams();
    } catch {
      showToast(t("common.network_error"), "error");
    }
  }

  async function addToCart(streamId) {
    if (!ensureAuthorized()) return;

    setCartAddingId(streamId);
    try {
      // 1) Пытаемся підготувати потік до оплати (ставить locked_until) — нова логіка
      let cartItem = { product_code: "MONITORING", stream_id: streamId, qty: 1, meta: {} };
      try {
        const prepRes = await authFetch(`${API}/streams-mgmt/prepare-for-checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ stream_id: streamId }),
        });

        if (prepRes.ok) {
          const prep = await prepRes.json().catch(() => ({}));
          if (prep?.cart_item) cartItem = prep.cart_item;
        } else if (prepRes.status !== 404 && prepRes.status !== 405) {
          // якщо сервіс доступний, але повернув помилку — показуємо її і не падаємо в стару логіку
          const d = await prepRes.json().catch(() => ({}));
          showToast(d?.detail || `${prepRes.status} ${prepRes.statusText}`, "error");
          return;
        }
        // 404/405 => сервісного роуту нема — тихо йдемо старим шляхом
      } catch {
        // мережевий збій на prepare — тихо йдемо старим шляхом
      }

      // 2) Додаємо до кошика (старий стабільний шлях)
      const addRes = await authFetch(`${API}/cart/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cartItem),
      });

      if (addRes.ok) {
        window.dispatchEvent(new Event("cart:refresh"));
        showToast(t("silence.toast.added_to_cart"), "info");
      } else {
        const d = await addRes.json().catch(() => ({}));
        showToast(d?.detail || `${addRes.status} ${addRes.statusText}`, "error");
      }
    } catch {
      showToast(t("common.network_error"), "error");
    } finally {
      setCartAddingId(null);
    }
  }

  // ----------------- СПОСТЕРІГАЧІ (invites) -----------------
  const API_ROOT = API;

  function assertApiConfigured() {
    if (!API_ROOT || !/^https?:\/\//i.test(API_ROOT)) {
      console.error("API_BASE_URL некоректен:", API_BASE_URL);
      showToast(t("silence.errors.api_url_not_set"), "error");
      return false;
    }
    return true;
  }

  const api = (path) => `${API_ROOT}${path}`;
  const LIST_URL = api("/streams/my_invites");
  const ADD_URL = api("/invites/invite");
  const DELETE_URL = api("/invites/invite/delete");
  const invitesLoadingRef = useRef(false);

  async function fetchList(silent = false) {
    if (!assertApiConfigured()) return;
    if (invitesLoadingRef.current) return;
    invitesLoadingRef.current = true;
    if (!silent) setLoading(true);
    try {
      if (!isAuth) {
        setList([]);
        return;
      }
      const res = await authFetch(LIST_URL);
      if (res.ok) {
        const json = await res.json();
        setList(
          Array.isArray(json)
            ? json.map((x) => ({ email: x.email, active: Boolean(x.active) }))
            : []
        );
      } else {
        setList([]);
      }
    } catch {
      setList([]);
    } finally {
      if (!silent) setLoading(false);
      invitesLoadingRef.current = false;
    }
  }

  async function addObserver() {
    if (!assertApiConfigured()) return;
    if (!ensureAuthorized()) return;
    setAdding(true);
    try {
      const res = await authFetch(ADD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail.trim() }),
      });
      if (res.ok) {
        setModalOpen(false);
        setNewEmail("");
        await fetchList();
      } else {
        const data = await res.json().catch(() => ({}));
        const detail = data?.detail;
        // ⛔ DEMO: глобальный перехватчик уже покажет модалку — тост не нужен
        if (
          res.status === 403 &&
          detail &&
          (detail.code === "DEMO_ADD_BLOCKED" || detail?.error === "DEMO_ADD_BLOCKED")
        ) {
          return;
        }
        showToast((detail || `${res.status} ${res.statusText}`), "error");
      }
    } catch {
      showToast(t("common.network_error"), "error");
    }
    setAdding(false);
  }

  async function removeObserver(email) {
    if (!assertApiConfigured()) return;
    if (!ensureAuthorized()) return;
    try {
      const res = await authFetch(DELETE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        await fetchList();
        setNewEmail("");
        console.log("Сотрудник удалён:", email);
      } else {
        const data = await res.json().catch(() => ({}));
        showToast((data && data.detail) || `${res.status} ${res.statusText}`, "error");
      }
    } catch {
      showToast(t("common.network_error"), "error");
    }
  }

  useEffect(() => {
    if (isAuth) {
      fetchList();
    } else {
      setList([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // Поллинг списку спостерігачів (60с), пауза у фоні, миттєвий рефреш при фокусі
  useEffect(() => {
    if (!isAuth) return;
    const tick = () => {
      if (document.hidden || !isAuth) return;
      fetchList(true);
    };
    const id = setInterval(tick, 60000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // --- РЕНДЕР ---
  return (
    <>
      {/* Оверлей логина */}
      {showLoginAlert && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            zIndex: 9999,
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              maxWidth: 420,
              width: "100%",
              background: "#fff",
              borderRadius: 15,
              boxShadow: "0 2px 16px #0002",
              padding: "0 0 32px 0",
            }}
          >
            <div
            style={{
              background: "#00723a",
              color: "#fff",
              padding: "18px 0",
              borderRadius: "15px 15px 0 0",
              textAlign: "center",
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: "1px",
            }}
          >
              {t("radio.login_required.title")}
          </div>
          <div style={{ textAlign: "center", margin: "30px 0 26px 0", fontSize: 17 }}>
              {t("radio.login_required.desc")}
          </div>
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => setShowLoginAlert(false)}
              style={{
                  minWidth: 120,
                  padding: "12px 0",
                  background: "#00723a",
                  color: "#fff",
                  borderRadius: 7,
                  border: "none",
                  fontSize: 17,
                  fontWeight: 600,
                cursor: "pointer",
                marginTop: 8,
              }}
            >
                {t("common.ok")}
            </button>
          </div>
        </div>
      </div>
    )}

      <AddStreamModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        url={url}
        onUrlChange={handleUrlChange}
        isValid={isValid}
        checking={checking}
        checkError={checkError}
        streamName={streamName}
        setStreamName={setStreamName}
        onAdd={handleAddStream}
      />

      {/* === БЛОК 1: ПОТОКИ === */}
      {!loadingStreams && streams.length === 0 ? (
        <div
          style={{
            maxWidth: 700,
            margin: "48px auto",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 2px 12px #0001",
            padding: 36,
          }}
        >
          <h2 style={{ fontWeight: 800, fontSize: 30, marginBottom: 8 }}>
            {t("silence.page.title_empty")}
          </h2>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ color: "#333", whiteSpace: "pre-line" }}>
              {t("silence.page.empty_hint")}
            </div>
            <button
              onClick={() => {
                if (!ensureAuthorized()) return;
                if (isDemo) {
                  window.dispatchEvent(new CustomEvent("demo:block", { detail: { action: "add_stream" } }));
                  return;
                }
                setUrl("");
                setStreamName("");
                setIsValid(false);
                setCheckError("");
                setShowAddModal(true);
              }}
              style={{
                padding: "10px 18px",
                fontSize: 16,
                borderRadius: 8,
                border: "none",
                background: "#00723a",
                color: "#fff",
                fontWeight: 700,
                whiteSpace: "nowrap",
                width: 220,
                cursor: "pointer",
              }}
              title={t("silence.streams.add_btn")}
            >
              {t("silence.streams.add_btn")}
            </button>
          </div>
          <hr style={{ border: 0, borderTop: "1px solid #e6e6e6", margin: "6px 0 0" }} />
        </div>
      ) : (
        <div
          style={{
            maxWidth: 700,
            margin: "48px auto",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 2px 12px #0001",
            padding: 36,
            fontFamily: "inherit",
          }}
        >
          <h2 style={{ fontWeight: 800, fontSize: 30, marginBottom: 22 }}>
            {t("silence.streams.title")}
          </h2>
          <div
            style={{
              maxHeight: "55vh",
              overflowY: "auto",
              paddingRight: 0,
              overscrollBehavior: "contain",
            }}
          >
            {loadingStreams && <div>{t("common.loading")}</div>}
            {!loadingStreams &&
              streams.map((stream, i) => {
                // ==== Логика кнопки "Оплата" ====
                const now = Date.now();
                const paidUntil = stream.paid_until ? new Date(stream.paid_until).getTime() : null;
                const testUntil = stream.test_until ? new Date(stream.test_until).getTime() : null;
                const lockedUntil = stream.locked_until ? new Date(stream.locked_until).getTime() : null;
                const statusText =
                  stream.paid_until && new Date(stream.paid_until) > new Date()
                    ? t("silence.streams.paid_until", {
                        dt: fmtDateTime(stream.paid_until, locale),
                      })
                    : stream.is_active && stream.test_until
                    ? t("silence.streams.test_until", {
                        dt: fmtDateTime(stream.test_until, locale),
                      })
                    : !stream.is_active && stream.locked_until
                    ? t("silence.streams.locked_until", {
                        dt: fmtDateTime(stream.locked_until, locale),
                      })
                    : "";

                // Показываем кнопку если:
                // - поток заблокирован и ещё не истёк locked_until
                // - или за 12 часов до конца оплаты/теста (если поток активный)
                const isPendingPayment =
                  (!stream.is_active && lockedUntil && lockedUntil > now)
                  || (stream.is_active && (
                    (paidUntil && paidUntil - now <= 12 * 3600 * 1000 && paidUntil > now)
                    ||
                    (testUntil && testUntil - now <= 12 * 3600 * 1000 && testUntil > now)
                  ));

                return (
                  <div
                    key={stream.id ?? stream.stream_url ?? i}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "18px 0",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
                      <span style={{ fontWeight: 700, fontSize: 18 }}>{stream.name}</span>
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          border: stream.is_active ? "2px solid #5c5" : "2px solid #c55",
                          display: "inline-block",
                        }}
                      />
                      <span
                        style={{
                          marginLeft: 12,
                          color: stream.is_active ? "#666" : "#e54848",
                          fontSize: 15,
                        }}
                      >
                        {statusText}
                      </span>
                      {isPendingPayment && (
                        <button
                          onClick={() => addToCart(stream.id)}
                          disabled={cartAddingId === stream.id}
                          style={{
                            marginLeft: "auto",
                            padding: "7px 26px",
                            fontSize: 15,
                            background: "#00723a",
                            color: "#fff",
                            borderRadius: 7,
                            border: "none",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            width: 180,
                            textAlign: "center",
                            cursor: cartAddingId === stream.id ? "wait" : "pointer",
                            opacity: cartAddingId === stream.id ? 0.7 : 1,
                          }}
                          title={t("silence.cart.title")}
                        >
                          {cartAddingId === stream.id
                            ? t("silence.cart.adding")
                            : t("silence.cart.add_to_cart")}
                        </button>
                      )}
                    </div>
                    <div style={{ color: "#444", fontSize: 15, marginLeft: 2 }}>{stream.stream_url}</div>
                  </div>
                );
              })}
          </div>
          <div style={{ marginTop: 20, textAlign: "right" }}>
            <button
              onClick={() => {
                if (!ensureAuthorized()) return;
                if (isDemo) {
                  window.dispatchEvent(new CustomEvent("demo:block", { detail: { action: "add_stream" } }));
                  return;
                }
                setUrl("");
                setStreamName("");
                setIsValid(false);
                setCheckError("");
                setShowAddModal(true);
              }}
              style={{
                padding: "10px 18px",
                fontSize: 16,
                borderRadius: 8,
                border: "none",
                background: "#00723a",
                color: "#fff",
                fontWeight: 700,
                whiteSpace: "nowrap",
                width: 220,
                cursor: "pointer",
              }}
            >
              {t("silence.streams.add_btn")}
            </button>
          </div>
        </div>
      )}

      {/* === БЛОК 2: ДОДАТКОВІ СПОСТЕРІГАЧІ === */}
      <AddObserverModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setNewEmail("");
        }}
        value={newEmail}
        onChange={setNewEmail}
        onAdd={addObserver}
        adding={adding}
      />

      <div
        style={{
          maxWidth: 700,
          margin: "48px auto",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 12px #0001",
          padding: 36,
        }}
      >
        <h2 style={{ fontWeight: 800, fontSize: 30, marginBottom: 8 }}>
          {t("silence.observers.title")}
        </h2>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              color: "#333",
              flex: "1 1 auto",
              lineHeight: 1.3,
              whiteSpace: "pre-line",
            }}
          >
            {t("silence.observers.desc")}
          </div>
          <button
            onClick={() => {
              if (!ensureAuthorized()) return;
              if (isDemo) {
                // Демо-акаунт: не открываем форму — показываем общий баннер
                window.dispatchEvent(
                  new CustomEvent("demo:block", { detail: { action: "add_observer" } })
                );
                return;
              }
              setModalOpen(true);
            }}
            disabled={list.length >= 2}
            style={{
              marginLeft: "auto",
              padding: "10px 18px",
              fontSize: 16,
              borderRadius: 8,
              border: "none",
              background: "#00723a",
              color: "#fff",
              fontWeight: 700,
              whiteSpace: "nowrap",
              width: 220,
              cursor: list.length >= 2 ? "not-allowed" : "pointer",
              opacity: list.length >= 2 ? 0.6 : 1,
            }}
            title={t("silence.observers.add_btn")}
          >
            {t("silence.observers.add_btn")}
          </button>
        </div>
        <hr style={{ border: 0, borderTop: "1px solid #e6e6e6", margin: "6px 0 18px" }} />
        {loading ? (
          <div>{t("common.loading")}</div>
        ) : (
          <>
            {list.map((item) => (
              <div key={item.email} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <input
                  value={item.email}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    fontSize: 16,
                    borderRadius: 8,
                    border: "1px solid #bbb",
                    background: "#f9f9f9",
                  }}
                />
                <span
                  title={
                    item.active
                      ? t("silence.observers.tooltip_connected")
                      : t("silence.observers.tooltip_pending")
                  }
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: item.active ? "#42d96c" : "#e54848",
                    border: `2px solid ${item.active ? "#42d96c" : "#e54848"}`,
                  }}
                />
                <button
                  onClick={() => {
                    setPendingDeleteEmail(item.email);
                    setConfirmDeleteOpen(true);
                  }}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  title={t("common.delete")}
                >
                  <DeleteOutlineIcon style={{ width: 32, height: 32, color: "#878787", marginLeft: -9 }} />
                </button>
              </div>
            ))}
            {list.length < 2 && (
              <div style={{ marginTop: 12, color: "#666", fontSize: 14 }}>
                {t("silence.observers.more_can_add", { count: 2 - list.length })}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        open={confirmAddOpen}
        title={t("common.confirm")}
        message={t("silence.confirm.add_paid_body")}
        confirmText={t("common.yes")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!ensureAuthorized()) {
            setConfirmAddOpen(false);
            return;
          }
          try {
            const resp = await authFetch(`${API}/streams/confirm`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ stream_url: pendingStream.url, name: pendingStream.name }),
            });
            if (resp.ok) {
              setUrl("");
              setStreamName("");
              setShowAddModal(false);
              await fetchStreams();
              showToast(t("silence.toast.add_stream_confirmed"), "success");
            } else {
              const d = await resp.json().catch(() => ({}));
              showToast(d?.detail || "Помилка підтвердження додавання потоку", "error");
            }
          } catch {
            showToast(t("common.network_error"), "error");
          } finally {
            setConfirmAddOpen(false);
            setPendingStream({ url: "", name: "" });
          }
        }}
        onCancel={() => {
          setConfirmAddOpen(false);
          setPendingStream({ url: "", name: "" });
        }}
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        title={t("common.confirm")}
        message={t("silence.confirm.delete_observer", { email: pendingDeleteEmail })}
        confirmText={t("common.yes")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          await removeObserver(pendingDeleteEmail);
          setPendingDeleteEmail(null);
          setConfirmDeleteOpen(false);
        }}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setPendingDeleteEmail(null);
        }}
      />

      {/* === ТОСТЫ === */}
      <Toast
        open={toast.open}
        text={toast.text}
        type={toast.type}
        onClose={() => setToast({ open: false, text: "", type: "info" })}
      />
    </>
  );
}
